import { Bell, Gift, Sparkles } from 'lucide-react';

/** Server-side one-time bonus for agreeing to both channels (services/marketing_consent_bonus.py). */
export const MARKETING_CONSENT_BONUS = 100;

export interface MarketingConsent {
  sms: boolean;
  email: boolean;
}

interface MarketingConsentFieldsProps {
  value: MarketingConsent;
  onChange: (next: MarketingConsent) => void;
  /** Smaller, framed variant for forms; "card" is the signup default. */
  variant?: 'card' | 'plain';
  idPrefix?: string;
  showSelectAll?: boolean;
  /** Highlight the one-time bonus; off where the member may already have received it. */
  showBonus?: boolean;
}

export function MarketingBonusBadge() {
  return (
    <div className="flex items-center gap-2 rounded-lg bg-amber-500 px-3 py-2 text-sm font-bold text-white shadow-sm">
      <Gift className="h-4 w-4 shrink-0" />
      문자·이메일 모두 동의하면 {MARKETING_CONSENT_BONUS}포인트를 바로 드려요
    </div>
  );
}

/**
 * Opt-in block for advertising notifications (new features, events, point
 * perks). Both boxes start unchecked by law; the copy and the explicit "전체선택"
 * button do the persuading. Shared by the signup pages, the account page
 * and the post-login prompt.
 */
export function MarketingConsentFields({
  value,
  onChange,
  variant = 'card',
  idPrefix = 'marketing-consent',
  showSelectAll = true,
  showBonus = false,
}: MarketingConsentFieldsProps) {
  const all = value.sms && value.email;
  const boxCls = 'h-4 w-4 accent-amber-500 cursor-pointer';
  const wrapperCls =
    variant === 'card'
      ? 'rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-4 space-y-3'
      : 'space-y-3';

  return (
    <div className={wrapperCls} data-testid="marketing-consent-fields">
      {showBonus && <MarketingBonusBadge />}
      <div className="flex items-start gap-2.5">
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
        <div>
          <p className="text-sm font-semibold text-forest-900">AutoQM 소식 받기</p>
          <p className="text-xs text-forest-500 mt-0.5">
            새 기능 출시·라이브 설명회·이벤트·무료 포인트 혜택을 가장 먼저 알려드려요.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-semibold text-forest-800">수신 채널 선택</span>
        {showSelectAll && <button
          type="button"
          aria-pressed={all}
          onClick={() => onChange({ sms: !all, email: !all })}
          className="rounded-lg border border-amber-200 bg-white px-3 py-1.5 text-xs font-semibold text-forest-700 hover:bg-amber-50"
        >
          {all ? '전체해제' : '전체선택'}
        </button>}
      </div>

      <div className="ml-6 space-y-2">
        <label htmlFor={`${idPrefix}-sms`} className="flex items-center gap-2 text-sm text-forest-700 cursor-pointer">
          <input
            id={`${idPrefix}-sms`}
            type="checkbox"
            className={boxCls}
            checked={value.sms}
            onChange={(e) => onChange({ ...value, sms: e.target.checked })}
          />
          <Bell className="h-3.5 w-3.5 text-forest-400" />
          문자(SMS)로 받기
        </label>
        <label htmlFor={`${idPrefix}-email`} className="flex items-center gap-2 text-sm text-forest-700 cursor-pointer">
          <input
            id={`${idPrefix}-email`}
            type="checkbox"
            className={boxCls}
            checked={value.email}
            onChange={(e) => onChange({ ...value, email: e.target.checked })}
          />
          <Gift className="h-3.5 w-3.5 text-forest-400" />
          이메일로 받기
        </label>
      </div>

      <p className="text-[11px] leading-relaxed text-forest-400">
        광고성 정보 수신 동의이며 선택 사항이에요. 동의하지 않아도 가입과 서비스 이용에 제한이 없고, 언제든 회원정보에서 바꿀 수 있어요.
      </p>
    </div>
  );
}
